import { ChevronDown } from "lucide-react";
import type { ReactNode } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface QuickMenuProps<T extends string> {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  trigger: ReactNode;
}

/** A badge that opens a single-choice menu (quick status / priority change). */
export const QuickMenu = <T extends string>({
  label,
  value,
  options,
  onChange,
  trigger,
}: QuickMenuProps<T>) => (
  <DropdownMenu>
    <DropdownMenuTrigger
      aria-label={label}
      title={label}
      className="inline-flex items-center gap-0.5 rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      {trigger}
      <ChevronDown aria-hidden className="size-3.5 text-muted-foreground" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="min-w-40">
      <DropdownMenuRadioGroup
        value={value}
        onValueChange={(next) => onChange(next as T)}
      >
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {options.map((option) => (
          <DropdownMenuRadioItem key={option.value} value={option.value}>
            {option.label}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </DropdownMenuContent>
  </DropdownMenu>
);
